import Link from 'found/Link';
import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { createPaginationContainer, graphql } from 'react-relay';

import BadgeActiveTimeRange from 'src/components/Badge/BadgeActiveTimeRange';
import LoadMoreButton from 'src/components/LoadMoreButton';
import Loading from 'src/components/Loading';
import UUID from 'src/components/UUID';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import logger from 'src/util/logger';

/**
 * Description
 * @param {object} props
 * @param {object} props.relay
 * @param {object} props.viewer
 * @returns {React.ReactComponentElement} - AdminCommunityList component
 */
function AdminCommunityList({ relay, viewer = null }) {
  const loadMore = () => {
    if (!relay.hasMore() || relay.isLoading()) {
      return;
    }

    relay.loadMore(
      50,
      (error) => { logger.error(error); },
    );
  };

  const intl = useIntl();

  if (!viewer) {
    return <Loading />;
  }

  const { communities } = viewer;

  // Page and header text
  const pageTitle = intl.formatMessage({
    id: 'admin.admin_community.admin_community_list.page.title',
    defaultMessage: 'Enosi Admin - Communities',
  });
  const headerText = intl.formatMessage({
    id: 'admin.admin_community.admin_community_list.header.text',
    defaultMessage: 'Communities',
  });

  // Breadcrumb labels
  const breadcrumbAdmin = intl.formatMessage({
    id: 'admin.admin_community.admin_community_list.breadcrumbs.admin.label',
    defaultMessage: 'Admin',
  });
  const breadcrumbCommunities = intl.formatMessage({
    id: 'admin.admin_community.admin_community_list.breadcrumbs.communities.label',
    defaultMessage: 'Communities',
  });

  // Table headers
  const headerStatus = intl.formatMessage({
    id: 'admin.admin_community.admin_community_list.table.header.status.label',
    defaultMessage: 'Status',
  });
  const headerTitle = intl.formatMessage({
    id: 'admin.admin_community.admin_community_list.table.header.title.label',
    defaultMessage: 'Title',
  });
  const headerId = intl.formatMessage({
    id: 'admin.admin_community.admin_community_list.table.header.id.label',
    defaultMessage: 'ID',
  });
  const headerExtIdTitle = intl.formatMessage({
    id: 'admin.admin_community.admin_community_list.table.header.external_identifier.abbr.title',
    defaultMessage: 'External identifier',
  });
  const headerExtIdLabel = intl.formatMessage({
    id: 'admin.admin_community.admin_community_list.table.header.external_identifier.label',
    defaultMessage: 'Ext ID',
  });
  const headerPropertyCount = intl.formatMessage({
    id: 'admin.admin_community.admin_community_list.table.header.property_count.label',
    defaultMessage: 'Property Count',
  });

  // Load more button text
  const copyLoadMore = intl.formatMessage({
    id: 'admin.admin_community.admin_community_list.load_more_button.state_load_more.label',
    defaultMessage: 'Load more...',
  });
  const copyNoMore = intl.formatMessage({
    id: 'admin.admin_community.admin_community_list.load_more_button.state_no_more.label',
    defaultMessage: 'No more communities to load',
  });

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: breadcrumbAdmin, path: '/admin' },
          { name: breadcrumbCommunities },
        ]}
      />

      <div className="clearfix">
        <h1 className="float-start">{headerText}</h1>
      </div>

      <div className="mt-4 mb-4 card">
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{headerStatus}</th>
                <th scope="col">{headerTitle}</th>
                <th scope="col">{headerId}</th>
                <th scope="col">
                  <abbr title={headerExtIdTitle}>{headerExtIdLabel}</abbr>
                </th>
                <th scope="col">{headerPropertyCount}</th>
              </tr>
            </thead>
            <tbody>
              {
                // properties && <pre>{JSON.stringify(properties, null, 2)}</pre>
                communities && communities.edges && communities.edges.map((edge) => {
                  const { node } = edge;
                  const {
                    uuid, title, externalIdentifier, active,
                  } = node;

                  return (
                    <tr key={`admin-community-${uuid}`}>
                      <td>
                        <BadgeActiveTimeRange active={active} />
                      </td>
                      <th scope="row">
                        <Link to={`/admin/communities/${uuid}`}>
                          <div>{title}</div>
                        </Link>
                      </th>
                      <td colSpan={2}>
                        <UUID uuid={uuid} />
                        <br />
                        {`(${externalIdentifier})`}
                      </td>
                      <td aria-label="property-count" />
                    </tr>
                  );
                })
              }
            </tbody>
          </table>
        </div>
        <div className="card-footer">
          <LoadMoreButton
            copyLoadMore={copyLoadMore}
            copyNoMore={copyNoMore}
            onClick={loadMore}
            hasNext={relay.hasMore()}
          />
        </div>
      </div>
    </>
  );
}

AdminCommunityList.propTypes = {
  relay: PropTypes.shape({
    hasMore: PropTypes.func,
    isLoading: PropTypes.func,
    loadMore: PropTypes.func,
  }).isRequired,
  viewer: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

export default createPaginationContainer(
  AdminCommunityList,
  {
    viewer: graphql`
      fragment AdminCommunityList_viewer on Viewer {
        id
        viewerUser {
          email
          givenName
          familyName
        }
        communities(
          first: $count
          after: $cursor
        ) @connection(key: "AdminCommunityList_communities") {
          edges {
            cursor
            node {
              uuid
              title
              externalIdentifier
              active { start finish }
            }
          }
          pageInfo {
            startCursor
            endCursor
            hasNextPage
            hasPreviousPage
          }
        }
      }
    `,
  },
  {
    direction: 'forward',
    getConnectionFromProps(props) {
      return props.viewer.communities;
    },
    // This is also the default implementation of `getFragmentVariables` if it isn't provided.
    getFragmentVariables(prevVars, totalCount) {
      return {
        ...prevVars,
        count: totalCount,
      };
    },
    getVariables(props, { count, cursor }) {
      return {
        count,
        cursor,
      };
    },
    query: graphql`
    # Pagination query to be fetched upon calling 'loadMore'.
    # Notice that we re-use our fragment, and the shape of this query matches our fragment spec.
    query AdminCommunityList_Pagination_Query(
      $count: Int!
      $cursor: String
    ) {
      viewer {
        ...AdminCommunityList_viewer
      }
    }
  `,
  },
);
